import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Card from "components/ui/Card/Card";
import Alert from "components/ui/Alert/Alert";
import DataTable, { type DataTableColumn } from "components/ui/DataTable/DataTable";
import PaginationFooter from "components/ui/PaginationFooter/PaginationFooter";
import Modal from "components/ui/Modal/Modal";
import Icon from "components/ui/Icon/Icon";
import { useI18n } from "context/I18nContext";
import { useAuth } from "context/AuthContext";
import { normalizeRoleName, RoleName } from "constants/roles";
import {
  fetchCards,
  importCardsFile,
  type CardImportResponse,
  type CardSummary,
  type PaginationMeta,
} from "utils/cardsApi";
import { ApiError } from "utils/apiClient";
import {
  formatDate,
  toDateInputValue,
  toRangeEndIso,
  toRangeStartIso,
} from "utils/dateUtils";
import styles from "./CardsPage.module.scss";

const PAGE_SIZE = 10;
const DATE_FROM_PARAM = "fromDate";
const DATE_TO_PARAM = "toDate";

const formatOptionalValue = (value: unknown, fallback: React.ReactNode = "—") => {
  if (value === null || value === undefined) {
    return fallback;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || fallback;
  }
  return String(value);
};

const formatStatusValue = (statusName: unknown, fallbackId?: number | null) => {
  if (typeof statusName === "string") {
    const trimmed = statusName.trim();
    if (trimmed) {
      return trimmed;
    }
  } else if (typeof statusName === "number") {
    return String(statusName);
  }
  if (typeof fallbackId === "number") {
    return String(fallbackId);
  }
  return "—";
};

function CardsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const location = useLocation() as ReturnType<typeof useLocation> & {
    state?: { flash?: { variant: "success" | "error"; message: string } };
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Math.max(1, Number(searchParams.get("page")) || 1);
  const rawSearch = searchParams.get("search") ?? "";
  const searchTerm = rawSearch.trim();
  const fromDateParam = searchParams.get(DATE_FROM_PARAM) ?? "";
  const toDateParam = searchParams.get(DATE_TO_PARAM) ?? "";

  const [cards, setCards] = useState<CardSummary[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [searchInput, setSearchInput] = useState(rawSearch);
  const [fromDateInput, setFromDateInput] = useState(() => toDateInputValue(fromDateParam));
  const [toDateInput, setToDateInput] = useState(() => toDateInputValue(toDateParam));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState(location.state?.flash ?? null);
  const [reloadKey, setReloadKey] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<CardImportResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const searchCooldownRef = useRef<number | null>(null);
  const [searchLocked, setSearchLocked] = useState(false);
  const normalizedRoles = useMemo(
    () =>
      (user?.roles ?? [])
        .map((role) => normalizeRoleName(role.roleName))
        .filter((roleName): roleName is RoleName => Boolean(roleName)),
    [user],
  );
  const canImportCards = normalizedRoles.some((role) =>
    [RoleName.SUPER_ADMIN, RoleName.ADMIN].includes(role),
  );

  const acceptedFileTypes = ".csv,.xlsx,.xlsm,.xltx,.xltm";

  const columns: DataTableColumn<CardSummary>[] = useMemo(
    () =>
      [
        {
          id: "cardNumber",
          header: t("cardsPage.table.cardNumber"),
          accessorKey: "cardNumber",
        },
      {
        id: "statusName",
        header: t("cardsPage.table.status"),
        accessorKey: "statusName",
        cell: (value, row) => <span>{formatStatusValue(value, row.statusId)}</span>,
        align: "center",
      },
      {
        id: "accountName",
        header: t("cardsPage.table.accountName"),
        accessorKey: "accountName",
        cell: (value) => <span>{formatOptionalValue(value)}</span>,
      },
      {
        id: "accountNo",
        header: t("cardsPage.table.accountNo"),
        accessorKey: "accountNo",
        cell: (value) => <span>{formatOptionalValue(value)}</span>,
      },
      {
        id: "issueDate",
        header: t("cardsPage.table.issueDate"),
        accessorKey: "issueDate",
        cell: (value) => <span>{formatDate(value)}</span>,
      },
      {
        id: "expireDate",
        header: t("cardsPage.table.expireDate"),
        accessorKey: "expireDate",
        cell: (value) => <span>{formatDate(value)}</span>,
      },
      {
        id: "referenceId",
        header: t("cardsPage.table.referenceId"),
        accessorKey: "referenceId",
        cell: (value) => <span>{formatOptionalValue(value)}</span>,
      },
      {
        id: "action",
        header: t("cardsPage.table.action"),
        cell: (_, row) => (
          <button
            type="button"
            className={styles.actionButton}
            onClick={() =>
              navigate(`/cards/${row.id}/edit`, {
                state: {
                  from: { pathname: "/cards", search: location.search },
                },
              })
            }
          >
            {t("cardsPage.table.details")}
          </button>
        ),
          align: "center",
        },
      ] satisfies DataTableColumn<CardSummary>[],
    [t, navigate, location.search],
  );

  useEffect(() => {
    if (flash) {
      const timer = window.setTimeout(() => setFlash(null), 3000);
      return () => window.clearTimeout(timer);
    }
    return () => undefined;
  }, [flash]);

  useEffect(() => {
    if (location.state?.flash) {
      setFlash(location.state.flash);
      navigate(".", { replace: true, state: null });
    }
  }, [location, navigate]);

  useEffect(() => {
    setSearchInput(rawSearch);
  }, [rawSearch]);

  useEffect(() => {
    setFromDateInput(toDateInputValue(fromDateParam));
  }, [fromDateParam]);

  useEffect(() => {
    setToDateInput(toDateInputValue(toDateParam));
  }, [toDateParam]);

  useEffect(
    () => () => {
      if (searchCooldownRef.current) {
        window.clearTimeout(searchCooldownRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchCards({
          page: pageParam,
          pageSize: PAGE_SIZE,
          search: searchTerm || undefined,
          fromDate: fromDateParam || undefined,
          toDate: toDateParam || undefined,
          signal: controller.signal,
        });
        setCards(response.items);
        setMeta(response.meta);
      } catch (err) {
        if ((err as DOMException).name === "AbortError") {
          return;
        }
        setError((err as Error).message || t("cardsPage.error"));
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [pageParam, searchTerm, fromDateParam, toDateParam, t, reloadKey]);

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (searchLocked) {
      return;
    }
    setSearchLocked(true);
    if (searchCooldownRef.current) {
      window.clearTimeout(searchCooldownRef.current);
    }
    searchCooldownRef.current = window.setTimeout(() => setSearchLocked(false), 3000);

    const trimmed = searchInput.trim();
    const nextFromParam = toRangeStartIso(fromDateInput);
    const nextToParam = toRangeEndIso(toDateInput);
    if (
      trimmed === searchTerm &&
      (nextFromParam ?? "") === fromDateParam &&
      (nextToParam ?? "") === toDateParam
    ) {
      setReloadKey((prev) => prev + 1);
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (trimmed) {
      next.set("search", trimmed);
    } else {
      next.delete("search");
    }
    if (nextFromParam) {
      next.set(DATE_FROM_PARAM, nextFromParam);
    } else {
      next.delete(DATE_FROM_PARAM);
    }
    if (nextToParam) {
      next.set(DATE_TO_PARAM, nextToParam);
    } else {
      next.delete(DATE_TO_PARAM);
    }
    next.set("page", "1");
    setSearchParams(next, { replace: false });
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage === (meta?.page ?? pageParam)) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("page", String(nextPage));
    setSearchParams(next, { replace: false });
  };

  const resetImportState = () => {
    setImportFile(null);
    setImportError(null);
    setImportResult(null);
  };

  const handleOpenImport = () => {
    resetImportState();
    setImportOpen(true);
  };

  const handleCloseImport = () => {
    setImportOpen(false);
    resetImportState();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    setImportFile(nextFile);
    setImportError(null);
    setImportResult(null);
  };

  const handleImportSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!importFile) {
      setImportError(t("cardsPage.importModal.fileRequired"));
      fileInputRef.current?.focus();
      return;
    }
    setImportLoading(true);
    setImportError(null);
    try {
      const response = await importCardsFile(importFile);
      setImportResult(response);
      setReloadKey((prev) => prev + 1);
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null;
      setImportError(apiError?.message || (err as Error).message || t("cardsPage.importModal.error"));
      setImportResult(null);
    } finally {
      setImportLoading(false);
    }
  };
  useEffect(() => {
    if (!canImportCards && importOpen) {
      setImportFile(null);
      setImportError(null);
      setImportResult(null);
      setImportOpen(false);
    }
  }, [canImportCards, importOpen]);

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>{t("cardsPage.title")}</h1>
            <p className={styles.subtitle}>{t("cardsPage.subtitle")}</p>
          </div>
          <div className={styles.actions}>
            {canImportCards && (
              <button type="button" className={styles.secondaryButton} onClick={handleOpenImport}>
                {t("cardsPage.import")}
              </button>
            )}
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() =>
                navigate("/cards/new", {
                  state: {
                    from: { pathname: "/cards", search: location.search },
                  },
                })
              }
            >
              <Icon name="add" color="inherit" />
              {t("cardsPage.add")}
            </button>
          </div>
        </div>

        <form className={styles.searchBar} onSubmit={handleSearchSubmit}>
          <label className={styles.fieldGroup} htmlFor="cards-search">
            <span className={styles.fieldLabel}>{t("cardsPage.searchLabel")}</span>
            <input
              id="cards-search"
              className={styles.searchInput}
              type="search"
              placeholder={t("cardsPage.searchPlaceholder")}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>
          <label className={styles.fieldGroup} htmlFor="cards-from-date">
            <span className={styles.fieldLabel}>{t("cardsPage.filters.from")}</span>
            <input
              id="cards-from-date"
              className={styles.dateInput}
              type="date"
              value={fromDateInput}
              max={toDateInput || undefined}
              onChange={(event) => setFromDateInput(event.target.value)}
            />
          </label>
          <label className={styles.fieldGroup} htmlFor="cards-to-date">
            <span className={styles.fieldLabel}>{t("cardsPage.filters.to")}</span>
            <input
              id="cards-to-date"
              className={styles.dateInput}
              type="date"
              value={toDateInput}
              min={fromDateInput || undefined}
              onChange={(event) => setToDateInput(event.target.value)}
            />
          </label>
          <button type="submit" className={styles.searchButton} disabled={searchLocked}>
            {t("datatable.search")}
          </button>
        </form>

        {(flash || error) && (
          <div className={styles.alertStack}>
            {flash && (
              <Alert variant={flash.variant} dismissible onClose={() => setFlash(null)}>
                {flash.message}
              </Alert>
            )}
            {error && (
              <Alert variant="error" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </div>
        )}

        <DataTable
          columns={columns}
          data={cards}
          globalSearch={false}
          loading={loading}
          loadingText={t("cardsPage.loading")}
          emptyState={loading ? t("cardsPage.loading") : t("cardsPage.empty")}
          manualPagination={
            meta
              ? {
                  pageIndex: Math.max((meta.page ?? 1) - 1, 0),
                  pageCount: Math.max(meta.pages ?? 1, 1),
                  pageSize: meta.pageSize ?? PAGE_SIZE,
                  totalItems: meta.total ?? cards.length,
                  onPageChange: (nextIndex) => handlePageChange(nextIndex + 1),
                }
              : undefined
          }
          showPaginationInfo={false}
          showPaginationControls={false}
        />

        {meta && (
          <PaginationFooter
            totalLabel={t("accountsPage.meta.total")}
            pageLabel={t("accountsPage.meta.page")}
            pageSizeLabel={t("accountsPage.meta.pageSize")}
            total={meta.total ?? 0}
            page={meta.page ?? 1}
            pageCount={Math.max(meta.pages ?? 1, 1)}
            pageSize={meta.pageSize ?? PAGE_SIZE}
            previousLabel={t("datatable.previous")}
            nextLabel={t("datatable.next")}
            goToLabel={t("accountsPage.pagination.goToLabel")}
            goButtonLabel={t("accountsPage.pagination.goButton")}
            invalidMessage={(max) => t("accountsPage.pagination.invalidPage", { max })}
            onPageChange={handlePageChange}
          />
        )}
      </Card>
      <Modal
        open={importOpen && canImportCards}
        onClose={handleCloseImport}
        title={t("cardsPage.importModal.title")}
        description={t("cardsPage.importModal.description")}
        size="lg"
      >
        <form className={styles.importForm} onSubmit={handleImportSubmit}>
          <p className={styles.helperText}>{t("cardsPage.importModal.instructions")}</p>
          <p className={styles.helperText}>{t("cardsPage.importModal.note")}</p>
          <label className={styles.importField}>
            <span className={styles.fieldLabel}>{t("cardsPage.importModal.fileLabel")}</span>
            <input
              ref={fileInputRef}
              className={styles.fileInput}
              type="file"
              accept={acceptedFileTypes}
              onChange={handleFileChange}
              disabled={importLoading}
            />
            <span className={styles.fileName}>
              {importFile?.name ?? t("cardsPage.importModal.filePlaceholder")}
            </span>
            <span className={styles.helperText}>{t("cardsPage.importModal.fileHint")}</span>
          </label>
          {importError && <p className={styles.errorText}>{importError}</p>}
          <div className={styles.importActions}>
            <button
              type="button"
              className={styles.backButton}
              onClick={handleCloseImport}
              disabled={importLoading}
            >
              {t("cardsPage.importModal.cancel")}
            </button>
            <button type="submit" className={styles.primaryButton} disabled={importLoading}>
              {importLoading
                ? t("cardsPage.importModal.submitting")
                : t("cardsPage.importModal.submit")}
            </button>
          </div>
        </form>
        {importResult && (
          <div className={styles.importSummary}>
            <p className={styles.summaryText}>
              {t("cardsPage.importModal.summary", {
                created: importResult.created,
                skipped: importResult.skipped,
              })}
            </p>
            {importResult.errors.length > 0 ? (
              <>
                <h4 className={styles.errorHeading}>
                  {t("cardsPage.importModal.errorsTitle")}
                </h4>
                <ul className={styles.errorList}>
                  {importResult.errors.map((item, index) => (
                    <li
                      key={`${item.rowNumber}-${item.cardNumber ?? index}`}
                      className={styles.errorItem}
                    >
                      <div className={styles.errorRow}>
                        {t("cardsPage.importModal.errorRow", { row: item.rowNumber })}
                      </div>
                      <div className={styles.errorMessage}>{item.message}</div>
                      <div className={styles.errorMeta}>
                        {item.cardNumber && (
                          <span>
                            {t("cardsPage.importModal.errorCard", { card: item.cardNumber })}
                          </span>
                        )}
                        {item.referenceId && (
                          <span>
                            {t("cardsPage.importModal.errorReference", {
                              reference: item.referenceId,
                            })}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className={styles.helperText}>{t("cardsPage.importModal.noErrors")}</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default CardsPage;
